<template>
  <div class="login-overlay" @click.self="close">
    <div class="login-card">

      <div class="login-header">
        <h2>{{ t('attachmentTitle') }}</h2>
        <button class="close-btn" @click="close">&#10005;</button>
      </div>

      <div class="form">

        <textarea
            v-model="text"
            :placeholder="t('writeMessage')"
            rows="3"
        />

        <div
            class="dropzone"
            :class="{ dragover: dragOver }"
            @click="triggerPick"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
        >
          <input
              ref="fileInput"
              type="file"
              class="hidden-input"
              @change="onFileChange"
          />

          <div v-if="file" class="file-row">
            <div class="file-info">
              <span class="file-name">{{ file.name }}</span>
              <span class="file-size">{{ formatSize(file.size) }}</span>
            </div>
            <button class="remove-btn" @click.stop="removeFile">
              {{ t('removeFile') }}
            </button>
          </div>

          <div v-else class="drop-hint">{{ t('dropZoneHint') }}</div>
        </div>

        <div v-if="errorMessage" class="error-message">{{ errorMessage }}</div>

        <button :disabled="!file || busy" @click="submit">
          {{ t('send') }}
        </button>

        <button class="secondary-btn" @click="close" :disabled="busy">
          {{ t('cancel') }}
        </button>

      </div>
    </div>

    <!-- Upload-Gate: a client without a password must set one before uploading.
         The chosen file is kept in state and the upload retries after the password is set. -->
    <PasswordSetupDialog
        v-if="needPassword"
        variant="upload"
        :service="service"
        @done="onPasswordDone"
        @cancel="onPasswordCancel"
    />
  </div>
</template>

<script>
import { useI18n } from "../i18n.js"
import PasswordSetupDialog from "./PasswordSetupDialog.vue"

const MAX_FILE_SIZE = 25 * 1024 * 1024 // 25 MB

export default {
  name: "ChatAttachmentDialog",

  components: { PasswordSetupDialog },

  props: {
    service: { type: Object, required: true },
    connectionId: { type: [String, Number], required: true },
    initialText: { type: String, default: "" }
  },

  emits: ["sent", "close"],

  setup() {
    const { t } = useI18n()
    return { t }
  },

  data() {
    return {
      text: this.initialText,
      file: null,
      busy: false,
      dragOver: false,
      errorMessage: "",
      needPassword: false
    }
  },

  mounted() {
    window.addEventListener("keydown", this.onKeydown)
  },

  beforeUnmount() {
    window.removeEventListener("keydown", this.onKeydown)
  },

  methods: {
    onKeydown(e) {
      if (e.key === "Escape") this.close()
    },

    triggerPick() {
      this.$refs.fileInput?.click()
    },

    onFileChange(e) {
      const f = e.target.files && e.target.files[0]
      if (f) this.setFile(f)
    },

    onDrop(e) {
      this.dragOver = false
      const f = e.dataTransfer?.files && e.dataTransfer.files[0]
      if (f) this.setFile(f)
    },

    setFile(f) {
      if (f.size > MAX_FILE_SIZE) {
        this.errorMessage = this.t("fileTooLarge").replace("{max}", this.formatSize(MAX_FILE_SIZE))
        return
      }
      this.errorMessage = ""
      this.file = f
    },

    removeFile() {
      this.file = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ""
    },

    formatSize(bytes) {
      if (bytes < 1024) return `${bytes} B`
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    },

    async submit() {
      if (!this.file || this.busy) return
      this.busy = true
      this.errorMessage = ""
      try {
        // text is optional; the backend accepts a file with an empty caption
        const msgs = await this.service.sendMessageWithFile(this.connectionId, this.text, this.file)
        this.$emit("sent", msgs)
      } catch (e) {
        if (e && e.code === "PASSWORD_REQUIRED") {
          // Keep the file; ask the user to set a password, then retry automatically.
          this.needPassword = true
        } else {
          this.errorMessage = this.t("attachmentFailed")
        }
        this.busy = false
      }
    },

    onPasswordDone() {
      // Password set -> retry the upload with the file still in state.
      this.needPassword = false
      this.submit()
    },

    onPasswordCancel() {
      // Discard the file; the chat stays usable. Show the "upload not started" notice.
      this.needPassword = false
      this.file = null
      if (this.$refs.fileInput) this.$refs.fileInput.value = ""
      this.errorMessage = this.t("uploadLaterNotice")
    },

    close() {
      if (this.busy) return
      // Hand the (possibly edited) caption back so the draft is preserved.
      this.$emit("close", this.text)
    }
  }
}
</script>

<style scoped>
.login-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.login-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.login-header h2 { margin: 0; font-size: 18px; }

.close-btn {
  border: none;
  background: none;
  font-size: 20px;
  cursor: pointer;
  color: #666;
}
.close-btn:hover { color: #000; }

.login-card {
  width: min(460px, calc(100vw - 32px));
  background: white;
  padding: 24px;
  border-radius: 12px;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

textarea {
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #ddd;
  font-family: inherit;
  font-size: 16px;
  resize: vertical;
  line-height: 1.4;
}

.dropzone {
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: border-color .15s, background .15s;
  background: #f8fafc;
}

.dropzone:hover { border-color: #94a3b8; }

.dropzone.dragover {
  border-color: #2563eb;
  background: #eff6ff;
}

.hidden-input { display: none; }

.drop-hint {
  color: #64748b;
  font-size: 14px;
}

.file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.file-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
}

.file-name {
  font-weight: 600;
  color: #1a1a2e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 260px;
}

.file-size {
  font-size: 12px;
  color: #64748b;
}

.remove-btn {
  flex-shrink: 0;
  padding: 6px 12px;
  background: #f0f2f5;
  color: #1a1a2e;
  border: none;
  border-radius: 6px;
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
}
.remove-btn:hover { background: #e4e8ee; }

button {
  padding: 12px;
  background: #2563eb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  cursor: pointer;
}

button:disabled {
  background: #c7c9d1;
  cursor: not-allowed;
}

.secondary-btn {
  background: #f0f2f5;
  color: #1a1a2e;
}

.secondary-btn:hover:not(:disabled) { background: #e4e8ee; }

.error-message {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 10px 12px;
  border-radius: 6px;
  font-size: 13px;
}
</style>
